import type { ShadowPlan, ShadowPlanDriver, ShadowPlanStop } from '../../services/shadowPlans.api';
import type { Route, RouteStop } from '../../types';
import { formatTimeBaghdad } from '../../utils/date';
import { ROUTE_COLORS } from '../routes/routeColors';

export const VAN_LABEL: Record<ShadowPlanDriver['vehicleType'], string> = { SHIFT1: 'Shift 1', SHIFT2: 'Shift 2', COLD: 'Cold van' };

export const KIND_LABEL: Record<ShadowPlanStop['kind'], string> = { pickup: 'Pickup', rto: 'Return drop', visit: 'Pickup + return', cash: 'Cash' };

export const KIND_COLOR = {
  pickup: 'bg-blue-500 border-blue-600',
  rto: 'bg-amber-500 border-amber-600',
  visit: 'bg-purple-500 border-purple-600',
  cash: 'bg-green-500 border-green-600',
} as const;

const REASON_LABEL: Record<string, string> = {
  capacity: 'too many orders for one van',
  window: 'no van can reach it in its time window',
  duration: 'the trip is longer than one shift',
  return: 'the van cannot get back before the shift ends',
  not_routed: 'left out by the planner',
  no_van_type: 'no van type can take it',
};

export const reasonLabel = (r: string) => REASON_LABEL[r] ?? r;

// A shadow day can have 13+ vans; past Fleet's 10 route colours, add distinct dark ones so no two drivers share one.
const DRIVER_COLORS = [...ROUTE_COLORS, '#0F766E', '#7C2D12', '#1E3A8A', '#BE123C', '#4D7C0F', '#64748B', '#A16207', '#111827'];

/** Drivers by first stop, each with its map colour (the same colour on the timeline and the map). */
export function colouredDrivers(plan: ShadowPlan): (ShadowPlanDriver & { color: string })[] {
  return [...plan.drivers]
    .sort((a, b) => a.startAt.localeCompare(b.startAt) || a.label.localeCompare(b.label))
    .map((d, i) => ({ ...d, color: DRIVER_COLORS[i % DRIVER_COLORS.length] }));
}

/** "13 drivers: 5 Shift 1, 6 Shift 2, 2 Cold van · 190 stops: 64 booked, 126 expected" */
export function planLine(plan: ShadowPlan): string {
  const vans = (['SHIFT1', 'SHIFT2', 'COLD'] as const)
    .map((t) => [plan.drivers.filter((d) => d.vehicleType === t).length, VAN_LABEL[t]] as const)
    .filter(([n]) => n > 0)
    .map(([n, l]) => `${n} ${l}`);
  const stops = plan.drivers.reduce((n, d) => n + d.stops.length, 0);
  const expected = plan.drivers.reduce((n, d) => n + d.stops.filter((s) => s.isForecast).length, 0);
  return `${plan.drivers.length} drivers: ${vans.join(', ')} · ${stops} stops: ${stops - expected} booked, ${expected} expected`;
}

const pickupUids = (p: ShadowPlan) =>
  new Set(
    p.drivers.flatMap((d) =>
      d.stops.filter((s) => !s.isForecast && (s.kind === 'pickup' || s.kind === 'visit')).flatMap((s) => s.taskUids),
    ),
  );

export function summaryLine(morning: ShadowPlan | null, latest: ShadowPlan | null): string {
  if (!latest) return morning ? `Drivers ${morning.summary.drivers} · morning plan ${formatTimeBaghdad(morning.generatedAt)}` : '';
  const changed = `last change ${formatTimeBaghdad(latest.generatedAt)}`;
  if (!morning) return `Drivers ${latest.summary.drivers} · ${changed}`;
  const before = pickupUids(morning);
  const after = pickupUids(latest);
  const added = [...after].filter((u) => !before.has(u)).length;
  const cancelled = [...before].filter((u) => !after.has(u)).length;
  const m = morning.summary.drivers;
  const l = latest.summary.drivers;
  return [m === l ? `Drivers ${l}` : `Drivers ${m} → ${l}`, `${added} new pickups`, `${cancelled} cancelled`, changed].join(' · ');
}

export function toRouteMap(drivers: (ShadowPlanDriver & { color: string })[]): { routes: Route[]; stops: Map<string, RouteStop[]> } {
  // RouteMap reads only these fields; start_location_mode stays unset so a line starts at its first stop, not Fleet's warehouse.
  const routes = drivers.map((d) => ({ _id: d.label, route_color: d.color }) as Partial<Route>);
  const stops = new Map<string, RouteStop[]>(
    drivers.map((d) => [
      d.label,
      [...d.stops]
        .sort((a, b) => a.seq - b.seq)
        .map(
          (s) =>
            ({
              _id: `${d.label}-${s.seq}`,
              lat: s.lat,
              lng: s.lng,
              type: s.kind === 'visit' ? 'pickup' : s.kind,
              task_uid: s.taskUids[0] ?? '',
              eta: s.eta,
              merchant_name: s.merchantName ?? undefined,
              parcel_count: s.orders,
              service_time_min: 0,
            }) as Partial<RouteStop> as RouteStop,
        ),
    ]),
  );
  return { routes: routes as Route[], stops };
}

export const SHIFT_HOURS: Record<ShadowPlanDriver['vehicleType'], string> = {
  SHIFT1: 'Slots S1–S2 · 12:00–17:00',
  SHIFT2: 'Slots S3–S5 · 17:00–01:00',
  COLD: 'Refrigerated · cold and normal orders · 13:00–23:00',
};

export interface ShiftRow {
  type: ShadowPlanDriver['vehicleType'];
  drivers: number;
  firstStart: string;
  lastEnd: string;
  avgHours: number;
  stops: number;
  expected: number;
  orders: number;
  returns: number;
  cold: number;
}

/** One row per van type that has drivers: how many, when they work, how much they carry. */
export function shiftRows(plan: ShadowPlan): ShiftRow[] {
  return (['SHIFT1', 'SHIFT2', 'COLD'] as const).flatMap((type) => {
    const ds = plan.drivers.filter((d) => d.vehicleType === type);
    if (!ds.length) return [];
    const stops = ds.flatMap((d) => d.stops);
    const hours = ds.reduce((n, d) => n + (Date.parse(d.endAt) - Date.parse(d.startAt)) / 3_600_000, 0);
    return [{
      type,
      drivers: ds.length,
      firstStart: ds.map((d) => d.startAt).sort()[0],
      lastEnd: ds.map((d) => d.endAt).sort().at(-1)!,
      avgHours: Math.round((hours / ds.length) * 10) / 10,
      stops: stops.length,
      expected: stops.filter((s) => s.isForecast).length,
      orders: ds.reduce((n, d) => n + d.orders, 0),
      returns: stops.reduce((n, s) => n + s.rtoParcels, 0),
      cold: stops.filter((s) => s.isCold).length,
    }];
  });
}

/** DAG update window, UTC. It may move to 00:45 (next day); the window is measured from the op-day start, so it can pass midnight. */
export const UPDATE_WINDOW_UTC = { start: '08:00', end: '22:00' };
export const STALE_AFTER_MIN = 40;
const OP_DAY_START_UTC = '01:00';
const toMin = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3));
const sinceOpDayStart = (utcMin: number) => (utcMin - toMin(OP_DAY_START_UTC) + 1440) % 1440;

/** Age in whole minutes of the newest plan, only when it is over STALE_AFTER_MIN old inside the update window; else null. */
export function staleMinutes(plan: ShadowPlan | null, now: Date): number | null {
  if (!plan) return null;
  const t = sinceOpDayStart(now.getUTCHours() * 60 + now.getUTCMinutes());
  if (t < sinceOpDayStart(toMin(UPDATE_WINDOW_UTC.start)) || t > sinceOpDayStart(toMin(UPDATE_WINDOW_UTC.end))) return null;
  const age = Math.floor((now.getTime() - new Date(plan.generatedAt).getTime()) / 60_000);
  return age > STALE_AFTER_MIN ? age : null;
}
